import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  type SelectChangeEvent
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import QrCodeScannerOutlinedIcon from '@mui/icons-material/QrCodeScannerOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import TabletMacOutlinedIcon from '@mui/icons-material/TabletMacOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import type { SvgIconComponent } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import {
  CONTENT_CARD_ICON_PRESETS,
  type ContentCardIconKey
} from './contentCardIconPresets';
import { isImageUrl, isNonImagePageUrl } from './iconUrl';
import { ImageUploadService } from '../../../accounting/inventory/services/imageUploadService';

const ICON_COMPONENTS: Record<ContentCardIconKey, SvgIconComponent> = {
  mail: MailOutlineIcon,
  phone: PhoneOutlinedIcon,
  'map-pin': LocationOnOutlinedIcon,
  clock: AccessTimeOutlinedIcon,
  star: StarOutlineIcon,
  shield: SecurityOutlinedIcon,
  rocket: RocketLaunchOutlinedIcon,
  check: CheckCircleOutlineIcon,
  users: GroupsOutlinedIcon,
  building: BusinessOutlinedIcon,
  globe: LanguageOutlinedIcon,
  message: ChatBubbleOutlineIcon,
  truck: LocalShippingOutlinedIcon,
  'credit-card': CreditCardOutlinedIcon,
  heart: FavoriteBorderIcon,
  settings: SettingsOutlinedIcon,
  search: SearchOutlinedIcon,
  home: HomeOutlinedIcon,
  briefcase: WorkOutlineIcon,
  award: EmojiEventsOutlinedIcon,
  headset: HeadsetMicOutlinedIcon,
  handshake: HandshakeOutlinedIcon,
  'trending-up': TrendingUpOutlinedIcon,
  zap: BoltOutlinedIcon,
  target: TrackChangesOutlinedIcon,
  printer: PrintOutlinedIcon,
  monitor: DesktopWindowsOutlinedIcon,
  barcode: QrCodeScannerOutlinedIcon,
  wallet: AccountBalanceWalletOutlinedIcon,
  ticket: ConfirmationNumberOutlinedIcon,
  box: Inventory2OutlinedIcon,
  tablet: TabletMacOutlinedIcon
};

/**
 * "emoji" alanındaki değeri render eder: değer bir görsel linki ise <img>,
 * değilse emoji karakteri olarak gösterir.
 */
export const ContentCardEmojiValue: React.FC<{
  value?: string;
  size?: number;
  /** Daire içinde beyaz silüet gibi göster (yüklenen mono ikonlar için) */
  invertOnDark?: boolean;
}> = ({ value, size = 24, invertOnDark = false }) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  if (isImageUrl(raw)) {
    return (
      <Box
        component="img"
        src={raw}
        alt=""
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          display: 'inline-block',
          ...(invertOnDark ? { filter: 'brightness(0) invert(1)' } : {})
        }}
      />
    );
  }
  return (
    <Box component="span" sx={{ fontSize: size * 0.85, lineHeight: 1 }}>
      {raw}
    </Box>
  );
};

export const ContentCardPresetIcon: React.FC<{
  iconKey?: string;
  size?: number;
  compact?: boolean;
  color?: string;
}> = ({
  iconKey,
  size = 20,
  compact = false,
  color
}) => {
  if (!iconKey || !(iconKey in ICON_COMPONENTS)) return null;
  const Icon = ICON_COMPONENTS[iconKey as ContentCardIconKey];
  if (compact) {
    return <Icon sx={{ fontSize: size, color: color || '#64748b' }} />;
  }
  return (
    <Box
      sx={{
        width: Math.max(36, size + 12),
        height: Math.max(36, size + 12),
        borderRadius: 1,
        bgcolor: '#f8fafc',
        color: color || '#64748b',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}
    >
      <Icon sx={{ fontSize: size }} />
    </Box>
  );
};

interface ContentCardIconSelectProps {
  iconKey?: string;
  emoji?: string;
  onIconKeyChange: (iconKey: string) => void;
  onEmojiChange: (emoji: string) => void;
}

const ContentCardIconSelect: React.FC<ContentCardIconSelectProps> = ({
  iconKey = '',
  emoji = '',
  onIconKeyChange,
  onEmojiChange
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const selectValue = iconKey || '';
  const emojiIsImage = isImageUrl(emoji);
  const emojiIsPageLink = isNonImagePageUrl(emoji);

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const nextKey = event.target.value;
    onIconKeyChange(nextKey);
  };

  const handleIconFileUpload = async (file: File) => {
    try {
      setUploading(true);
      setUploadError('');
      // Önce lokal önizleme: upload callback'i iconKey temizlerken emoji'yi silmesin diye
      // sadece onEmojiChange kullanıyoruz (iconKey zaten orada temizleniyor).
      const localPreview = URL.createObjectURL(file);
      onEmojiChange(localPreview);

      const [uploaded] = await ImageUploadService.uploadImages([file]);
      if (uploaded?.originalUrl) {
        onEmojiChange(uploaded.originalUrl);
        URL.revokeObjectURL(localPreview);
      } else {
        setUploadError('İkon yüklenemedi. Lütfen tekrar deneyin.');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'İkon yüklenirken bir hata oluştu';
      setUploadError(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <FormControl fullWidth size="small">
        <InputLabel>İkon Seç</InputLabel>
        <Select
          label="İkon Seç"
          value={selectValue}
          onChange={handleSelectChange}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <Typography variant="body2" color="text.secondary">
                  İkon seçin
                </Typography>
              );
            }
            const preset = CONTENT_CARD_ICON_PRESETS.find((item) => item.key === selected);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <ContentCardPresetIcon iconKey={selected} size={22} />
                <Typography variant="body2">{preset?.label || selected}</Typography>
              </Box>
            );
          }}
          MenuProps={{
            PaperProps: {
              sx: { maxHeight: 320 }
            }
          }}
        >
          <MenuItem value="">
            <Typography variant="body2" color="text.secondary">
              İkon yok
            </Typography>
          </MenuItem>
          {CONTENT_CARD_ICON_PRESETS.map((preset) => (
            <MenuItem key={preset.key} value={preset.key}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ContentCardPresetIcon iconKey={preset.key} size={22} />
                <Typography variant="body2">{preset.label}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box
        sx={{
          mt: 1.25,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1
        }}
      >
        <TextField
          fullWidth
          size="small"
          label="veya emoji / ikon linki yapıştır"
          placeholder="Örn. 🚀 ya da https://.../icon.png"
          value={emoji}
          onChange={(e) => onEmojiChange(e.target.value)}
          inputProps={{ style: { fontSize: 18 } }}
          InputProps={{
            endAdornment: emojiIsImage ? (
              <Box
                component="img"
                src={String(emoji).trim()}
                alt=""
                sx={{ width: 28, height: 28, objectFit: 'contain', ml: 1, flexShrink: 0 }}
              />
            ) : undefined
          }}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <Button
          size="small"
          variant="outlined"
          component="label"
          startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          disabled={uploading}
          sx={{
            flexShrink: 0,
            whiteSpace: 'nowrap',
            height: 40,
            px: 1.5,
            alignSelf: { xs: 'stretch', sm: 'center' }
          }}
        >
          {uploading ? 'Yükleniyor...' : 'İkon Dosyası Yükle'}
          <input
            type="file"
            hidden
            accept="image/*,.svg,.png,.jpg,.jpeg,.webp,.gif"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                void handleIconFileUpload(file);
              }
              e.target.value = '';
            }}
          />
        </Button>
      </Box>

      {emojiIsImage && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, color: 'success.main' }}>
          <CheckCircleIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption">İkon görseli algılandı; kartta bu görsel gösterilecek.</Typography>
        </Box>
      )}

      {emojiIsPageLink && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 0.75, color: 'warning.main' }}>
          <BrokenImageOutlinedIcon sx={{ fontSize: 16, mt: '2px' }} />
          <Typography variant="caption">
            Bu bir ikon sayfası linki gibi görünüyor. İkonun kendisini almak için sitede ikona sağ tıklayıp
            “Resim adresini kopyala” deyin, doğrudan görsel (.png/.svg) linkini yapıştırın veya dosya olarak yükleyin.
          </Typography>
        </Box>
      )}

      {uploadError && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
          {uploadError}
        </Typography>
      )}
    </Box>
  );
};

export default ContentCardIconSelect;
